import React from 'react';
import { teboxDesignSystem } from '../../styles/tebox-design-system';
import { PLATFORM_UI_FONT_NAME } from '../../utils/fontFamily';

export interface FontOption {
  value: string;
  label: string;
  googleFont: string;
  category: 'serif' | 'sans-serif' | 'monospace';
  description: string;
}

export const FONT_OPTIONS: FontOption[] = [
  // Serif - Elegant & Classic
  { value: 'Castoro', label: 'Castoro', googleFont: 'Castoro', category: 'serif', description: 'Elegant serif' },
  { value: 'Playfair Display', label: 'Playfair Display', googleFont: 'Playfair+Display:wght@400;500;600;700', category: 'serif', description: 'Classic editorial' },
  { value: 'Lora', label: 'Lora', googleFont: 'Lora:wght@400;500;600;700', category: 'serif', description: 'Readable & warm' },
  { value: 'Merriweather', label: 'Merriweather', googleFont: 'Merriweather:wght@300;400;700', category: 'serif', description: 'Screen optimized' },
  { value: 'Libre Baskerville', label: 'Libre Baskerville', googleFont: 'Libre+Baskerville:wght@400;700', category: 'serif', description: 'Traditional book' },
  { value: 'PT Serif', label: 'PT Serif', googleFont: 'PT+Serif:wght@400;700', category: 'serif', description: 'Professional' },

  // Sans-Serif — Inter is the platform default; clubs may pick other Google fonts for booking
  {
    value: PLATFORM_UI_FONT_NAME,
    label: PLATFORM_UI_FONT_NAME,
    googleFont: 'Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900',
    category: 'sans-serif',
    description: 'Platform default UI sans',
  },
  { value: 'Google Sans Flex', label: 'Google Sans Flex', googleFont: 'Google+Sans+Flex:opsz,wght@8..144,100..1000', category: 'sans-serif', description: 'Google product sans' },
  { value: 'Raleway', label: 'Raleway', googleFont: 'Raleway:ital,wght@0,100..900;1,100..900', category: 'sans-serif', description: 'Elegant sans' },
  { value: 'DM Sans', label: 'DM Sans', googleFont: 'DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400', category: 'sans-serif', description: 'Humanist sans (Google DM Sans)' },
  { value: 'Roboto', label: 'Roboto', googleFont: 'Roboto:wght@300;400;500;700', category: 'sans-serif', description: 'Clean & modern' },
  { value: 'Open Sans', label: 'Open Sans', googleFont: 'Open+Sans:wght@300;400;500;600;700', category: 'sans-serif', description: 'Friendly & neutral' },
  { value: 'Montserrat', label: 'Montserrat', googleFont: 'Montserrat:wght@300;400;500;600;700', category: 'sans-serif', description: 'Geometric modern' },
  { value: 'Poppins', label: 'Poppins', googleFont: 'Poppins:wght@300;400;500;600;700', category: 'sans-serif', description: 'Contemporary' },
  { value: 'Nunito', label: 'Nunito', googleFont: 'Nunito:wght@300;400;500;600;700', category: 'sans-serif', description: 'Rounded & soft' },

  // Monospace - Tech & Unique
  { value: 'JetBrains Mono', label: 'JetBrains Mono', googleFont: 'JetBrains+Mono:wght@400;500;600;700', category: 'monospace', description: 'Developer favorite' },
  { value: 'Source Code Pro', label: 'Source Code Pro', googleFont: 'Source+Code+Pro:wght@400;500;600;700', category: 'monospace', description: 'Adobe classic' },
  { value: 'Fira Code', label: 'Fira Code', googleFont: 'Fira+Code:wght@400;500;600;700', category: 'monospace', description: 'Modern coding' },
  { value: 'IBM Plex Mono', label: 'IBM Plex Mono', googleFont: 'IBM+Plex+Mono:wght@400;500;600;700', category: 'monospace', description: 'Corporate tech' },
];

/**
 * Signup / SPIN quick-pick — a readable subset of `FONT_OPTIONS`.
 * The full typeface list remains in **Brand settings** in admin.
 */
export const SIGNUP_QUICK_PICK_FONT_VALUES = [
  'Castoro',
  'Playfair Display',
  'Lora',
  PLATFORM_UI_FONT_NAME,
  'Raleway',
  'DM Sans',
  'Poppins',
  'Open Sans',
  'Montserrat',
] as const satisfies ReadonlyArray<FontOption['value']>;

export const SIGNUP_QUICK_PICK_FONTS: readonly FontOption[] = SIGNUP_QUICK_PICK_FONT_VALUES.map(
  (value) => FONT_OPTIONS.find((f) => f.value === value) as FontOption
);

const LEGACY_BOOKING_FONT_ALIASES: Record<string, string> = {
  'Stack Sans Text': PLATFORM_UI_FONT_NAME,
  'Host Grotesk': PLATFORM_UI_FONT_NAME,
  TWKLausanne: PLATFORM_UI_FONT_NAME,
  'TWK Lausanne': PLATFORM_UI_FONT_NAME,
  Lexend: PLATFORM_UI_FONT_NAME,
  // Keep Raleway as a real brand option — do not alias it away
};

/**
 * Normalize persisted font labels so they match `FONT_OPTIONS[].value` (trim, legacy aliases).
 */
export function normalizeBookingBrandFontName(fontFamily: string | undefined | null): string {
  const t = typeof fontFamily === 'string' ? fontFamily.trim() : '';
  if (!t) return '';
  return LEGACY_BOOKING_FONT_ALIASES[t] ?? t;
}

/**
 * CSS `font-family` stack for booking/email previews — identical to font-name tiles in {@link FontSelector}.
 */
export function bookingBrandPreviewFontFamilyCss(fontFamily: string | undefined | null): string {
  const normalized = normalizeBookingBrandFontName(fontFamily);
  if (!normalized) return `'${PLATFORM_UI_FONT_NAME}', sans-serif`;
  const opt = FONT_OPTIONS.find((f) => f.value === normalized);
  if (opt) return `'${opt.value}', ${opt.category}`;
  return `'${normalized}', serif`;
}

interface FontSelectorProps {
  selectedFont: string;
  onFontChange: (font: string) => void;
}

const CATEGORIES = [
  { key: 'serif', label: 'Serif', description: 'Classic & Elegant' },
  { key: 'sans-serif', label: 'Sans-Serif', description: 'Modern & Clean' },
  { key: 'monospace', label: 'Monospace', description: 'Tech & Unique' },
] as const;

const FontSelector: React.FC<FontSelectorProps> = ({ selectedFont, onFontChange }) => {
  const ds = teboxDesignSystem;
  const ff = ds.typography.fontFamily;
  const normalizedSelection = normalizeBookingBrandFontName(selectedFont);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: ds.spacing.md }}>
      {CATEGORIES.map((category) => {
        const fontsInCategory = FONT_OPTIONS.filter((f) => f.category === category.key);

        return (
          <div key={category.key}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: ds.spacing.sm,
                marginBottom: ds.spacing.sm,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontFamily: ff,
                  fontSize: ds.typography.bodySmall.fontSize,
                  fontWeight: 500,
                  color: ds.typography.caption.color as string,
                  letterSpacing: '0.5px',
                }}
              >
                {category.label}
              </span>
              <span
                style={{
                  fontFamily: ff,
                  fontSize: ds.typography.caption.fontSize,
                  color: ds.colors.text.tertiary,
                }}
              >
                {category.description}
              </span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: ds.spacing.sm,
              }}
            >
              {fontsInCategory.map((font) => {
                const selected = normalizedSelection === font.value;
                return (
                  <button
                    key={font.value}
                    type="button"
                    onClick={() => onFontChange(font.value)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      padding: `${ds.spacing.sm} ${ds.spacing.md}`,
                      border: `1px solid ${selected ? (ds.typography.caption.color as string) : ds.colors.border.light}`,
                      borderRadius: ds.borderRadius.none,
                      backgroundColor: ds.colors.background.primary,
                      cursor: 'pointer',
                      transition: 'border-color 0.15s ease, background-color 0.15s ease',
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        marginBottom: 2,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: `'${font.value}', ${font.category}`,
                          fontSize: ds.typography.body.fontSize,
                          fontWeight: 500,
                          color: ds.colors.text.primary,
                        }}
                      >
                        {font.label}
                      </span>
                      {selected ? (
                        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M16.6667 5L7.50004 14.1667L3.33337 10"
                            stroke={ds.colors.text.primary}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : null}
                    </div>
                    <span
                      style={{
                        fontFamily: ff,
                        fontSize: ds.typography.caption.fontSize,
                        color: ds.colors.text.tertiary,
                      }}
                    >
                      {font.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FontSelector;
